import React, { useMemo } from 'react';
import { Card, Box, Typography, Stack, alpha, useTheme } from '@mui/material';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { ResponsiveContainer, AreaChart, Area } from 'recharts';

const buildFallbackSeries = (seed = 8) => {
  const arr = [];
  let v = seed;
  for (let i = 0; i < 12; i++) {
    v += Math.sin(i * 0.7 + seed) * 1.5 + Math.cos(i * 0.4) * 1.2;
    arr.push({ v: Math.max(1, Math.round(v * 10) / 10) });
  }
  return arr;
};

const KpiTile = ({ label, value, icon, color = '#FE5F01', growth, secondary, series }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const hasGrowth = typeof growth === 'number';
  const positive = (growth ?? 0) > 0;
  const negative = (growth ?? 0) < 0;

  const trendBg = positive
    ? isDark ? alpha(theme.palette.success.main, 0.16) : theme.palette.success.lighter
    : negative
      ? isDark ? alpha(theme.palette.error.main, 0.16) : theme.palette.error.lighter
      : isDark ? alpha(theme.palette.grey[500], 0.16) : alpha(theme.palette.grey[500], 0.1);

  const trendColor = positive
    ? isDark ? theme.palette.success.light : theme.palette.success.dark
    : negative
      ? isDark ? theme.palette.error.light : theme.palette.error.dark
      : theme.palette.text.secondary;

  const TrendIcon = positive ? TrendingUp : negative ? TrendingDown : Minus;

  const sparkData = useMemo(
    () => (Array.isArray(series) && series.length ? series : buildFallbackSeries(String(label || '').length || 5)),
    [series, label]
  );

  const gradientId = `kpi-grad-${String(label || 'x').replace(/\s+/g, '-').toLowerCase()}`;

  return (
    <Card
      sx={{
        width: '100%',
        maxWidth: '100%',
        p: { xs: 2, md: 2.5 },
        borderRadius: 3,
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        border: `1px solid ${theme.palette.divider}`,
        transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: isDark
            ? '0 12px 24px -4px rgba(0,0,0,0.5)'
            : `0 12px 24px -4px ${alpha(theme.palette.primary.main, 0.08)}`,
          borderColor: alpha(color, 0.4),
        },
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5} position="relative">
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 2,
            bgcolor: alpha(color, isDark ? 0.2 : 0.1),
            color: color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            border: `1px solid ${alpha(color, 0.2)}`,
          }}
        >
          {icon}
        </Box>
        {hasGrowth && (
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{
              bgcolor: trendBg,
              color: trendColor,
              px: 1,
              py: 0.35,
              borderRadius: 1.5,
              fontSize: '0.75rem',
              fontWeight: 700,
            }}
          >
            <TrendIcon size={13} strokeWidth={2.5} />
            <span>{positive ? '+' : ''}{growth}%</span>
          </Stack>
        )}
      </Stack>

      <Typography
        variant="caption"
        sx={{
          fontSize: '0.75rem',
          fontWeight: 700,
          color: 'text.secondary',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          display: 'block',
        }}
      >
        {label}
      </Typography>

      <Typography
        variant="h4"
        sx={{
          fontWeight: 800,
          lineHeight: 1.2,
          color: 'text.primary',
          mt: 0.5,
          fontSize: { xs: '1.45rem', md: '1.75rem' },
        }}
      >
        {value}
      </Typography>

      <Stack direction="row" alignItems="flex-end" justifyContent="space-between" mt={1.5} spacing={1}>
        {secondary ? (
          <Typography
            variant="caption"
            sx={{
              color: 'text.disabled',
              fontSize: '0.75rem',
              fontWeight: 500,
              flex: 1,
              minWidth: 0,
            }}
            noWrap
          >
            {secondary}
          </Typography>
        ) : (
          <span />
        )}
        <Box sx={{ width: 88, height: 36, flexShrink: 0 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={sparkData} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey="v"
                stroke={color}
                strokeWidth={2}
                fill={`url(#${gradientId})`}
                dot={false}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      </Stack>
    </Card>
  );
};

export default React.memo(KpiTile);
